import type { DocumentItem } from './model.js';

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

/** `2450000` -> `"2.4 MB"`. Uses 1024-based units, as file browsers do. */
export function formatBytes(bytes: number | undefined | null, locale?: string): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes === 0) return '0 B';

  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), SIZE_UNITS.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  // Bytes are never fractional; larger units get one decimal, dropped when .0.
  const digits = exponent === 0 ? 0 : value >= 100 ? 0 : 1;
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value);
  return `${formatted} ${SIZE_UNITS[exponent]}`;
}

function toDate(value: DocumentItem['updatedAt']): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Relative for anything under a week ("2 hours ago", "Yesterday"), absolute
 * beyond that ("Aug 20"). Matches what the reference UI shows.
 */
export function formatDate(value: DocumentItem['updatedAt'], locale?: string, now: Date = new Date()): string {
  const date = toDate(value);
  if (!date) return '';

  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.round(diffMs / 60_000);

  if (Math.abs(diffMinutes) < 60 * 24 * 7) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (Math.abs(diffMinutes) < 1) return rtf.format(0, 'minute');
    if (Math.abs(diffMinutes) < 60) return rtf.format(-diffMinutes, 'minute');
    const diffHours = Math.round(diffMinutes / 60);
    if (Math.abs(diffHours) < 24) return rtf.format(-diffHours, 'hour');
    return rtf.format(-Math.round(diffHours / 24), 'day');
  }

  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}
